/**
 * Pure H OS · Sprint 2 · User Profile page.
 * Composes Card, SectionHeader, Button, TextInput, TabBar, Field,
 * Toggle, Segmented, Kbd.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Sliders, Languages, Keyboard, LogOut } from "lucide-react";
import { usePureH } from "../context/PureHContext";
import { usePlatform } from "../context/PlatformContext";
import { Card, SectionHeader, Button, TextInput } from "../components/lib";
import { TabBar, Field, Toggle, Segmented, Kbd } from "../components/lib.overlay";
import { KEYBOARD_SHORTCUTS } from "../mocks/platformData";

type Tab = "profile" | "preferences" | "language" | "shortcuts" | "session";

export default function Profile() {
  const { user, logout } = usePureH();
  const { prefs, setLanguage, setDensity, setNotifPref, openShortcuts } = usePlatform();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("profile");

  const [name, setName]     = useState(user?.name ?? "");
  const [email, setEmail]   = useState(user?.email ?? "");
  const [role]              = useState(user?.role ?? "owner");

  const doLogout = () => { logout(); navigate("/pureh/login", { replace: true }); };

  return (
    <div data-testid="pureh-profile">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Account</div>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Profile</h1>
        <div className="mt-1 text-sm text-slate-500">Signed in as <span style={{ color: "var(--ph-ink-default)" }}>{user?.email}</span> · Role: {role}</div>
      </div>

      <div className="mb-6">
        <TabBar<Tab>
          testId="profile-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "profile",     label: "Profile"    },
            { key: "preferences", label: "Preferences" },
            { key: "language",    label: "Language"   },
            { key: "shortcuts",   label: "Shortcuts"  },
            { key: "session",     label: "Session"    },
          ]}
        />
      </div>

      {tab === "profile" && (
        <Card testId="profile-tab-profile">
          <SectionHeader eyebrow="Identity" title="Profile" action={<User size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Full name" inline>
            <div style={{ width: 260 }}><TextInput value={name} onChange={setName} testId="profile-name" /></div>
          </Field>
          <Field label="Email" inline>
            <div style={{ width: 260 }}><TextInput value={email} onChange={setEmail} testId="profile-email" /></div>
          </Field>
          <Field label="Role" hint="Managed by the Owner. Contact Hassan to change." inline>
            <span
              className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase font-semibold"
              style={{
                letterSpacing: "0.14em",
                background: "var(--ph-brand-tint)",
                color: "var(--ph-brand-primary)",
                borderRadius: "var(--ph-radius-sm)",
              }}
            >
              {role}
            </span>
          </Field>
          <div className="pt-4 flex justify-end gap-2">
            <Button variant="secondary" testId="profile-cancel">Cancel</Button>
            <Button testId="profile-save">Save changes</Button>
          </div>
        </Card>
      )}

      {tab === "preferences" && (
        <Card testId="profile-tab-preferences">
          <SectionHeader eyebrow="Interface" title="Preferences" action={<Sliders size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Density" hint="Comfortable spacing across every surface, or compact for information-dense screens." inline>
            <Segmented<typeof prefs.density>
              testId="pref-density"
              value={prefs.density}
              onChange={setDensity}
              options={[
                { value: "comfortable", label: "Comfortable" },
                { value: "compact",     label: "Compact"     },
              ]}
            />
          </Field>
          <div className="pt-4 pb-2 text-[10px] uppercase font-semibold" style={{ letterSpacing: "var(--ph-tracking-eyebrow)", color: "var(--ph-ink-muted)" }}>
            Notification channels
          </div>
          <Field label="Mentions" hint="When a teammate mentions you by name." inline>
            <Toggle checked={prefs.notifications.mentions} onChange={(v) => setNotifPref("mentions", v)} testId="pref-notif-mentions" />
          </Field>
          <Field label="Priority" hint="High-severity alerts and overdue items." inline>
            <Toggle checked={prefs.notifications.priority} onChange={(v) => setNotifPref("priority", v)} testId="pref-notif-priority" />
          </Field>
          <Field label="AI signals" hint="Discoveries and recommendations from AI teammates." inline>
            <Toggle checked={prefs.notifications.ai} onChange={(v) => setNotifPref("ai", v)} testId="pref-notif-ai" />
          </Field>
          <Field label="Approvals" hint="Requests waiting on your sign-off." inline>
            <Toggle checked={prefs.notifications.approvals} onChange={(v) => setNotifPref("approvals", v)} testId="pref-notif-approvals" />
          </Field>
          <Field label="System" hint="Digests, scheduled reports, housekeeping." inline>
            <Toggle checked={prefs.notifications.system} onChange={(v) => setNotifPref("system", v)} testId="pref-notif-system" />
          </Field>
        </Card>
      )}

      {tab === "language" && (
        <Card testId="profile-tab-language">
          <SectionHeader eyebrow="Locale" title="Language" action={<Languages size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Interface language" hint="Applies to menus, labels, and system copy. Data is not translated." inline>
            <Segmented<typeof prefs.language>
              testId="pref-language"
              value={prefs.language}
              onChange={setLanguage}
              options={[
                { value: "en", label: "English" },
                { value: "ar", label: "العربية" },
              ]}
            />
          </Field>
          <div className="pt-3 text-xs" style={{ color: "var(--ph-ink-muted)" }}>
            Arabic (RTL) rendering will ship when the AI Headquarters copy pack is finalised in Sprint 5.
          </div>
        </Card>
      )}

      {tab === "shortcuts" && (
        <Card testId="profile-tab-shortcuts">
          <SectionHeader
            eyebrow="Productivity"
            title="Keyboard Shortcuts"
            action={
              <button
                data-testid="open-shortcuts-modal"
                onClick={openShortcuts}
                className="text-xs font-medium"
                style={{ color: "var(--ph-brand-primary)" }}
              >
                Open modal
              </button>
            }
          />
          <div className="space-y-1">
            {KEYBOARD_SHORTCUTS.slice(0, 10).map((s, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-1.5"
                data-testid={`profile-shortcut-${s.keys.join("-")}`}
              >
                <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>{s.label}</div>
                <div className="flex items-center gap-1">
                  {s.keys.map((k, ki) => <Kbd key={ki}>{k}</Kbd>)}
                </div>
              </div>
            ))}
          </div>
          <div className="pt-3 text-xs" style={{ color: "var(--ph-ink-muted)" }}>
            <Keyboard size={11} className="inline mr-1" />
            Press <Kbd>⌘</Kbd><Kbd>/</Kbd> anywhere to open the full list.
          </div>
        </Card>
      )}

      {tab === "session" && (
        <Card testId="profile-tab-session">
          <SectionHeader eyebrow="Security" title="Session" action={<LogOut size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Signed in as" inline>
            <span className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>{user?.email}</span>
          </Field>
          <Field label="Session storage" hint="Sprint 1 authentication is mocked — session lives in localStorage under pureh:session:v1." inline>
            <span className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>localStorage</span>
          </Field>
          <div className="pt-4 flex justify-end">
            <Button variant="secondary" onClick={doLogout} testId="profile-logout">
              <LogOut size={13} /> Sign out
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
